import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

type CalendarEvent = {
  id: string;
  title: string;
  date: Date;
  endDate?: Date;
  time?: string;
  type: 'liturgy' | 'feast' | 'fast' | 'meeting' | 'youth';
  description?: string;
};

const GOOGLE_CALENDAR_ID = '30r61tr1g2f5loeqs3ulqtjfds@group.calendar.google.com';
const API_KEY = 'AIzaSyAeHQ7X5E_YFn940SpA9zrCxYv82FIJNVw';

interface WeeklyCalendarProps {
  compact?: boolean;
}

export default function WeeklyCalendar({ compact = false }: WeeklyCalendarProps) {
  const { t } = useTranslation();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  
  const weekDays = useMemo(() => {
    const days: Date[] = [];
    const startOfWeek = new Date(currentDate);
    const day = startOfWeek.getDay();
    startOfWeek.setDate(startOfWeek.getDate() - day);
    
    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      days.push(date);
    }
    return days;
  }, [currentDate]);

  // Fetch events from Google Calendar API
  useEffect(() => {
    const fetchEvents = async () => {
      setLoading(true);
      try {
        const weekStart = weekDays[0];
        const weekEnd = new Date(weekDays[6]);
        weekEnd.setHours(23, 59, 59, 999);

        const timeMin = weekStart.toISOString();
        const timeMax = weekEnd.toISOString();

        const url = `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(GOOGLE_CALENDAR_ID)}/events?key=${API_KEY}&timeMin=${timeMin}&timeMax=${timeMax}&singleEvents=true&orderBy=startTime&maxResults=50`;

        const response = await fetch(url);
        const data = await response.json();

        if (data.items && data.items.length > 0) {
          const parsed: CalendarEvent[] = data.items.map((item: { id: string; summary?: string; start: { dateTime?: string; date?: string }; end?: { dateTime?: string; date?: string }; description?: string }) => {
            const startStr = item.start.dateTime || item.start.date || '';
            const endStr = item.end?.dateTime || item.end?.date || '';
            const startDate = new Date(startStr);
            const endDate = endStr ? new Date(endStr) : undefined;

            let time: string | undefined;
            if (item.start.dateTime && item.end?.dateTime) {
              const startTime = startDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
              const endTime = endDate!.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
              time = `${startTime} - ${endTime}`;
            }

            return {
              id: item.id,
              title: item.summary || 'Event',
              date: startDate,
              endDate,
              time,
              type: determineEventType(item.summary || ''),
              description: item.description,
            };
          });

          setEvents(parsed);
        } else {
          setEvents([]);
        }
      } catch (error) {
        console.warn('Calendar temporarily unavailable:', error);
        setEvents([]);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, [weekDays]);

  // Determine event type based on title
  const determineEventType = (title: string): CalendarEvent['type'] => {
    const lowerTitle = title.toLowerCase();
    if (lowerTitle.includes('liturgy') || lowerTitle.includes('mass') || lowerTitle.includes('vespers') || lowerTitle.includes('tasbeha')) {
      return 'liturgy';
    } else if (lowerTitle.includes('feast') || lowerTitle.includes('theophany') || lowerTitle.includes('nativity') || lowerTitle.includes('resurrection')) {
      return 'feast';
    } else if (lowerTitle.includes('fast') || lowerTitle.includes('lent')) {
      return 'fast';
    } else if (lowerTitle.includes('youth') || lowerTitle.includes('sunday school')) {
      return 'youth';
    }
    return 'meeting';
  };

  const getEventsForDay = (date: Date) => {
    return events.filter(event => {
      const eventDate = new Date(event.date);
      return eventDate.toDateString() === date.toDateString();
    });
  };

  const getAllWeekEvents = () => {
    const allEvents: { event: CalendarEvent; date: Date }[] = [];
    weekDays.forEach(date => {
      const dayEvents = getEventsForDay(date);
      dayEvents.forEach(event => {
        allEvents.push({ event, date });
      });
    });
    return allEvents.sort((a, b) => a.date.getTime() - b.date.getTime());
  };

  const navigateWeek = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + (direction === 'next' ? 7 : -7));
    setCurrentDate(newDate);
    setSelectedDay(null);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
    setSelectedDay(null);
  };

  const formatMonthYear = () => {
    const firstDay = weekDays[0];
    const lastDay = weekDays[6];
    const options: Intl.DateTimeFormatOptions = { month: 'long', year: 'numeric' };
    
    if (firstDay.getMonth() === lastDay.getMonth()) {
      return firstDay.toLocaleDateString('en-US', options);
    }
    return `${firstDay.toLocaleDateString('en-US', { month: 'short' })} - ${lastDay.toLocaleDateString('en-US', options)}`;
  };

  const isToday = (date: Date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isSelected = (date: Date) => {
    return selectedDay?.toDateString() === date.toDateString();
  };

  const getEventColor = (type: string) => {
    switch (type) {
      case 'liturgy': return { bg: 'bg-amber-50', border: 'border-l-amber-500', text: 'text-amber-700', dot: 'bg-amber-500' };
      case 'feast': return { bg: 'bg-rose-50', border: 'border-l-rose-500', text: 'text-rose-700', dot: 'bg-rose-500' };
      case 'fast': return { bg: 'bg-stone-50', border: 'border-l-stone-500', text: 'text-stone-700', dot: 'bg-stone-500' };
      case 'meeting': return { bg: 'bg-teal-50', border: 'border-l-teal-500', text: 'text-teal-700', dot: 'bg-teal-500' };
      case 'youth': return { bg: 'bg-sky-50', border: 'border-l-sky-500', text: 'text-sky-700', dot: 'bg-sky-500' };
      default: return { bg: 'bg-gray-50', border: 'border-l-gray-500', text: 'text-gray-700', dot: 'bg-gray-500' };
    }
  };

  const getEventIcon = (type: string) => {
    switch (type) {
      case 'liturgy': return 'ri-goblet-fill';
      case 'feast': return 'ri-star-fill';
      case 'fast': return 'ri-moon-fill';
      case 'meeting': return 'ri-team-fill';
      case 'youth': return 'ri-group-fill';
      default: return 'ri-calendar-event-fill';
    }
  };

  const openGoogleCalendar = () => {
    navigate('/calendar');
  };

  const subscribeToCalendar = () => {
    window.open(`https://calendar.google.com/calendar/r?cid=${encodeURIComponent(GOOGLE_CALENDAR_ID)}`, '_blank');
  };

  const displayEvents = selectedDay ? getEventsForDay(selectedDay) : getAllWeekEvents().map(e => e.event);

  return (
    <div className="bg-white rounded-3xl shadow-lg overflow-hidden border border-gray-100">
      {/* Header */}
      <div className="px-4 sm:px-8 py-4 sm:py-6 border-b border-gray-100">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 sm:mb-6">
          <div className="min-w-0">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 truncate">{formatMonthYear()}</h2>
            <p className="text-gray-500 text-xs sm:text-sm mt-1">{t('weeklyCalendar.servicesEvents')}</p>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            <button
              onClick={subscribeToCalendar}
              className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line text-base sm:text-lg"></i>
              <span className="hidden sm:inline">{t('weeklyCalendar.subscribe')}</span>
              <span className="sm:hidden">{t('weeklyCalendar.subscribeShort')}</span>
            </button>
            <button
              onClick={openGoogleCalendar}
              className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm font-medium text-white bg-[#3d1a1e] hover:bg-[#2a1215] rounded-xl transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-google-fill text-base sm:text-lg"></i>
              <span className="hidden sm:inline">{t('weeklyCalendar.googleCalendar')}</span>
              <span className="sm:hidden">{t('weeklyCalendar.calendar')}</span>
            </button>
          </div>
        </div>

        {/* Week Navigation */}
        <div className="flex items-center gap-1.5 sm:gap-3">
          <button
            onClick={() => navigateWeek('prev')}
            className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-xl bg-gray-100 hover:bg-gray-200 transition-all cursor-pointer flex-shrink-0"
          >
            <i className="ri-arrow-left-s-line text-lg sm:text-xl text-gray-700"></i>
          </button>
          
          <div className="flex-1 grid grid-cols-7 gap-0.5 sm:gap-2">
            {weekDays.map((date, index) => {
              const today = isToday(date);
              const selected = isSelected(date);
              const hasEvents = getEventsForDay(date).length > 0;
              const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
              
              return (
                <button
                  key={index}
                  onClick={() => setSelectedDay(selected ? null : date)}
                  className={`relative flex flex-col items-center py-2 sm:py-3 px-0.5 sm:px-2 rounded-xl sm:rounded-2xl transition-all cursor-pointer ${
                    selected
                      ? 'bg-[#3d1a1e] text-white shadow-lg'
                      : today
                      ? 'bg-amber-50 text-amber-900'
                      : 'hover:bg-gray-50 text-gray-700'
                  }`}
                >
                  <span className={`text-[10px] sm:text-xs font-medium mb-0.5 sm:mb-1 ${selected ? 'text-white/70' : 'text-gray-400'}`}>
                    {dayNames[index]}
                  </span>
                  <span className={`text-sm sm:text-lg font-bold ${selected ? 'text-white' : ''}`}>
                    {date.getDate()}
                  </span>
                  {hasEvents && !selected && (
                    <div className="flex gap-0.5 mt-1 sm:mt-1.5">
                      {getEventsForDay(date).slice(0, 3).map((event, i) => (
                        <span key={i} className={`w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full ${getEventColor(event.type).dot}`}></span>
                      ))}
                    </div>
                  )}
                  {hasEvents && selected && (
                    <>
                      <span className="hidden sm:inline text-[10px] sm:text-xs text-white/70 mt-0.5 sm:mt-1">{getEventsForDay(date).length} {t('weeklyCalendar.events')}</span>
                      <span className="sm:hidden text-[10px] text-white/70 mt-0.5">{getEventsForDay(date).length}</span>
                    </>
                  )}
                </button>
              );
            })}
          </div>
          
          <button
            onClick={() => navigateWeek('next')}
            className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-xl bg-gray-100 hover:bg-gray-200 transition-all cursor-pointer flex-shrink-0"
          >
            <i className="ri-arrow-right-s-line text-lg sm:text-xl text-gray-700"></i>
          </button>
          
          <button
            onClick={goToToday}
            className="px-2.5 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm font-medium text-[#3d1a1e] bg-amber-50 hover:bg-amber-100 rounded-xl transition-all cursor-pointer whitespace-nowrap flex-shrink-0"
          >
            {t('weeklyCalendar.today')}
          </button>
        </div>
      </div>

      {/* Events List */}
      <div className={`p-6 ${compact ? 'max-h-[400px]' : 'max-h-[500px]'} overflow-y-auto`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">
            {selectedDay 
              ? selectedDay.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })
              : t('weeklyCalendar.thisWeekSchedule')
            }
          </h3>
          {selectedDay && (
            <button 
              onClick={() => setSelectedDay(null)}
              className="text-sm text-[#3d1a1e] hover:underline cursor-pointer whitespace-nowrap"
            >
              {t('weeklyCalendar.viewAllWeek')}
            </button>
          )}
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <i className="ri-loader-4-line text-3xl text-gray-400 animate-spin"></i>
            </div>
            <p className="text-gray-500">{t('weeklyCalendar.loadingEvents')}</p>
          </div>
        ) : displayEvents.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-gray-100 rounded-2xl">
              <i className="ri-calendar-line text-3xl text-gray-400"></i>
            </div>
            <p className="text-gray-500">{t('weeklyCalendar.noEvents')}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {displayEvents.map((event, index) => {
              const colors = getEventColor(event.type);
              const eventDate = new Date(event.date);
              
              return (
                <div
                  key={`${event.id}-${index}`}
                  className={`group flex items-start gap-4 p-4 rounded-2xl ${colors.bg} border-l-4 ${colors.border} hover:shadow-md transition-all cursor-pointer`}
                >
                  <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${colors.text} bg-white/80`}>
                    <i className={`${getEventIcon(event.type)} text-lg`}></i>
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h4 className={`font-semibold ${colors.text}`}>{event.title}</h4>
                        {event.description && (
                          <p className="text-sm text-gray-600 mt-0.5">{event.description}</p>
                        )}
                      </div>
                      {!selectedDay && (
                        <span className="text-xs font-medium text-gray-400 whitespace-nowrap">
                          {eventDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                        </span>
                      )}
                    </div>
                    
                    {event.time && (
                      <div className="flex items-center gap-1.5 mt-2">
                        <i className="ri-time-line text-sm text-gray-400"></i>
                        <span className="text-sm text-gray-600">{event.time}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer Legend */}
      <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
        <div className="flex flex-wrap items-center justify-center gap-6">
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-amber-500"></span>
            <span className="text-xs text-gray-600">{t('weeklyCalendar.liturgy')}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-rose-500"></span>
            <span className="text-xs text-gray-600">{t('weeklyCalendar.feast')}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-stone-500"></span>
            <span className="text-xs text-gray-600">{t('weeklyCalendar.fast')}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-teal-500"></span>
            <span className="text-xs text-gray-600">{t('weeklyCalendar.meeting')}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-sky-500"></span>
            <span className="text-xs text-gray-600">{t('weeklyCalendar.youth')}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
